// src/components/common/Navbar.jsx (Updated with Profile Switcher)
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  Settings, 
  BarChart3, 
  Brain,
  Crown,
  Bell,
  Clock,
  AlertTriangle,
  CheckCircle,
  CreditCard,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PaymentService } from '../../services/paymentService';
import ProfileSwitcher from './ProfileSwitcher';
import logo from '../../assets/images/logo.png';

const Navbar = ({ variant = 'default' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const { currentUser, userProfile, activeContext, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser && variant === 'dashboard') {
      loadSubscriptionStatus();
    }
  }, [currentUser, variant]);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const subscriptions = await PaymentService.getUserSubscriptions(currentUser.uid);
      const activeSubscription = subscriptions.find(sub => sub.status === 'active');
      
      if (activeSubscription) {
        const now = new Date();
        const expiresAt = new Date(activeSubscription.endDate);
        const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
        
        setSubscriptionStatus({
          ...activeSubscription,
          daysUntilExpiry,
          isExpiringSoon: daysUntilExpiry <= 7 && daysUntilExpiry > 0,
          isExpired: daysUntilExpiry <= 0
        });
      } else {
        setSubscriptionStatus({ status: 'none' });
      }
    } catch (error) {
      console.error('Error loading subscription status:', error);
      setSubscriptionStatus({ status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getSubscriptionBadge = () => {
    if (!subscriptionStatus || subscriptionStatus.status === 'none') {
      return (
        <Link 
          to="/pricing"
          className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-md text-xs font-medium hover:from-orange-600 hover:to-red-600 transition-all"
        >
          <Crown className="w-3 h-3" />
          <span className="hidden sm:inline">Upgrade</span>
        </Link>
      );
    }

    if (subscriptionStatus.isExpired) {
      return (
        <Link 
          to="/subscription"
          className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-md text-xs font-medium hover:bg-red-200 transition-colors"
        >
          <AlertTriangle className="w-3 h-3" />
          <span className="hidden sm:inline">Expired</span>
        </Link>
      );
    }

    if (subscriptionStatus.isExpiringSoon) {
      return (
        <Link 
          to="/subscription"
          className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-md text-xs font-medium hover:bg-orange-200 transition-colors"
        >
          <Clock className="w-3 h-3" />
          <span className="hidden sm:inline">{subscriptionStatus.daysUntilExpiry}d left</span>
        </Link>
      );
    }

    return (
      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs font-medium">
        <CheckCircle className="w-3 h-3" />
        <span className="hidden sm:inline">Pro</span>
      </div>
    );
  };

  if (variant === 'dashboard') {
    return (
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="SmartRegister" className="h-10 w-auto" />
              <span className="text-xl font-bold text-gray-900">SmartRegister</span>
            </Link>

            {/* Profile Switcher - Center */}
            <div className="hidden md:flex flex-1 justify-center">
              <ProfileSwitcher />
            </div>

            {/* Right Side - User Menu & Subscription Status */}
            <div className="flex items-center gap-3">
              {/* Subscription Badge */}
              {!loading && getSubscriptionBadge()}

              {/* Notifications */}
              {subscriptionStatus?.isExpiringSoon && (
                <button 
                  onClick={() => navigate('/subscription')}
                  className="p-2 text-orange-600 hover:text-orange-800 relative"
                >
                  <Bell className="w-5 h-5" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                </button>
              )}

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 bg-[#6e55a3] rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="hidden lg:block text-left">
                    <div className="text-sm font-medium text-gray-900">
                      {userProfile?.firstName} {userProfile?.lastName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {activeContext ? `${activeContext.role} at ${activeContext.name}` : userProfile?.position}
                    </div>
                  </div>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="text-sm font-medium text-gray-900">
                          {userProfile?.firstName} {userProfile?.lastName}
                        </div>
                        <div className="text-xs text-gray-500">{userProfile?.email}</div>
                        {activeContext && (
                          <div className="text-xs text-blue-600 mt-1">
                            Currently: {activeContext.name}
                          </div>
                        )}
                      </div>

                      {/* Menu Items */}
                      <Link
                        to="/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                      
                      <Link
                        to="/subscription"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <CreditCard className="w-4 h-4" />
                        Subscription
                      </Link>

                      <div className="border-t border-gray-100 my-1"></div>
                      
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          handleSignOut();
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <div className="md:hidden">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200">
              <div className="py-4 space-y-3">
                {/* Mobile Profile Switcher */}
                <div className="px-2">
                  <ProfileSwitcher />
                </div>
                
                <div className="border-t border-gray-100 pt-3">
                  <Link
                    to="/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Settings
                  </Link>
                  <Link
                    to="/subscription"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Subscription
                  </Link>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleSignOut();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
    );
  }

  // Default navbar for landing page
  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="SmartRegister" className="h-10 w-auto" />
            
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-gray-600 hover:text-[#6e55a3] transition-colors">Features</a>
            <a href="#ai-reports" className="text-gray-600 hover:text-[#6e55a3] transition-colors">AI Reports</a>
            <a href="#benefits" className="text-gray-600 hover:text-[#6e55a3] transition-colors">Benefits</a>
            <a href="#testimonials" className="text-gray-600 hover:text-[#6e55a3] transition-colors">Reviews</a>
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {currentUser ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <BarChart3 className="w-4 h-4" />
                  Dashboard
                </Link>
                <button 
                  onClick={handleSignOut}
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Sign In
                </Link>
                <Link 
                  to="/register" 
                  className="bg-[#6e55a3] text-white px-4 py-2 rounded-md hover:bg-[#6156a6] transition-colors flex items-center gap-2"
                >
                  <Brain className="w-4 h-4" />
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-600 hover:text-gray-900"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-3">
              <a href="#features" className="text-gray-600 hover:text-[#6e55a3] px-2 py-1">Features</a>
              <a href="#ai-reports" className="text-gray-600 hover:text-[#6e55a3] px-2 py-1">AI Reports</a>
              <a href="#benefits" className="text-gray-600 hover:text-[#6e55a3] px-2 py-1">Benefits</a>
              <a href="#testimonials" className="text-gray-600 hover:text-[#6e55a3] px-2 py-1">Reviews</a>
              <div className="flex flex-col gap-2 px-2 pt-2">
                {currentUser ? (
                  <>
                    <Link 
                      to="/dashboard" 
                      className="text-center py-2 text-gray-600"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <button 
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleSignOut();
                      }}
                      className="text-center py-2 text-gray-600"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/login" 
                      className="text-center py-2 text-gray-600"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link 
                      to="/register" 
                      className="bg-[#6e55a3] text-white px-4 py-2 rounded-md text-center"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;